import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Sparkles, Eye, ArrowLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ConversationSharePage() {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/conversations/share/${shareId}`);
        setShare(data);
      } catch (err) {
        setError(err.response?.status === 404 ? "This conversation was not found." : "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4]">
        <Loader2 className="w-8 h-8 text-[#3c8a6e] animate-spin" />
      </div>
    );
  }

  if (error || !share) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f7f4] px-6">
        <div className="text-center max-w-md">
          <MessageCircle className="w-12 h-12 text-[#3c8a6e]/30 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[#2d3b35] mb-2">Not Found</h1>
          <p className="text-[#888] mb-6">{error || "This conversation doesn't exist."}</p>
          <Link to="/" className="text-[#3c8a6e] hover:underline text-sm">Go to Omni</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f7f4]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-[#e8e4df]">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#e8f0ec] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#3c8a6e]" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-[#2d3b35]">{share.module_label}</h1>
              <p className="text-[11px] text-[#888]">
                {share.user_name} &amp; Omni &middot; {share.message_count} messages
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[#888]">
            <Eye className="w-3.5 h-3.5" />
            <span>{share.views} views</span>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="max-w-2xl mx-auto px-6 py-8 space-y-4">
        {share.messages.map((msg, idx) => {
          const isUser = msg.role === "user";
          return (
            <div
              key={idx}
              data-testid={`shared-msg-${idx}`}
              className={cn(
                "flex gap-3",
                isUser ? "flex-row-reverse" : "flex-row"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold",
                isUser
                  ? "bg-[#e8f0ec] text-[#3c8a6e]"
                  : "bg-[#e0eef5] text-[#357a9b]"
              )}>
                {isUser ? share.user_name?.[0]?.toUpperCase() : "O"}
              </div>
              <div className={cn(
                "max-w-[80%] p-4 rounded-2xl",
                isUser
                  ? "bg-[#e8f0ec] rounded-tr-md"
                  : "bg-white border border-[#e8e4df] rounded-tl-md"
              )}>
                <p className={cn(
                  "text-[11px] font-semibold mb-1.5",
                  isUser ? "text-[#3c8a6e]" : "text-[#357a9b]"
                )}>
                  {isUser ? share.user_name : "Omni"}
                </p>
                <p className="text-sm text-[#2d3b35] leading-relaxed whitespace-pre-wrap">
                  {msg.content}
                </p>
                {msg.timestamp && (
                  <p className="text-[10px] text-[#aaa] mt-2">
                    {msg.timestamp.slice(0, 16).replace("T", " ")}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </main>

      {/* Footer CTA */}
      <footer className="border-t border-[#e8e4df] bg-white/80 backdrop-blur-md py-6">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-sm text-[#2d3b35] mb-3">
            Want your own AI companion?
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#3c8a6e] text-white rounded-full text-sm font-medium hover:bg-[#2d7a5e] transition-all"
          >
            <Sparkles className="w-4 h-4" /> Try Omni for Free
          </Link>
          <p className="text-[11px] text-[#aaa] mt-4">
            Omni is an AI companion. Not a licensed professional.
          </p>
        </div>
      </footer>
    </div>
  );
}
